import React, { useState } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import Drawer from "@mui/material/Drawer";
import Box from "@mui/material/Box";
import Slide from "@mui/material/Slide";
import StorefrontIcon from "@mui/icons-material/Storefront";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import KeyboardArrowUpRoundedIcon from "@mui/icons-material/KeyboardArrowUpRounded";
import RewardsBox from "./RewardsBox";
import "../styles_web/Animations.css";
import css from "../styles_web/HeaderProfileMenu.module.css";
import { useNavigate } from "react-router-dom";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import Grid from "@mui/material/Grid";
import Image from "../components/shared/Image";

/************************************/
/* Header Profile Mobile Drawer Menu */
/************************************/
const HeaderProfileDrawerMenu = ({
  isOpen,
  onClosed,
  userName,
  profileTitle,
  storeName,
  storeLogo,
  profileMenu,
  navMenu,
  authMenu,
  rewards,
  onSignInOut,
}) => {
  return (
    <Drawer
      open={isOpen}
      variant="persistent"
      ModalProps={{
        keepMounted: true,
      }}
      transitionDuration={500}
      onClose={onClosed}
    >
      <Box
        sx={{
          display: "grid",
          minHeight: "100vh",
          gridTemplateRows: "1fr 80px",
        }}
      >
        <div className={css["drawer-wrapper"]}>
          <div className={css["drawer-header"]}>
            <IconButton onClick={onClosed} className={css[`drawer-close`]}>
              <CloseIcon></CloseIcon>
            </IconButton>
          </div>
          <HeaderProfileMobileMenu
            onClosed={onClosed}
            userName={userName}
            profileTitle={profileTitle}
            storeName={storeName}
            storeLogo={storeLogo}
            profileMenu={profileMenu}
            navMenu={navMenu}
            authMenu={authMenu}
            rewards={rewards}
            onSignInOut={onSignInOut}
          />
        </div>
      </Box>
    </Drawer>
  );
};

HeaderProfileDrawerMenu.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClosed: PropTypes.func.isRequired,
  onSignInOut: PropTypes.func,
  userName: PropTypes.string,
  profileTitle: PropTypes.string,
  storeName: PropTypes.string,
  storeLogo: PropTypes.string,
  profileMenu: PropTypes.array,
  navMenu: PropTypes.array,
  authMenu: PropTypes.array,
  rewards: PropTypes.object,
};

export default HeaderProfileDrawerMenu;

/************************************/
/* Header Profile Mobile Menu */
/************************************/
const HeaderProfileMobileMenu = ({
  onClosed,
  userName,
  profileTitle,
  storeLogo,
  storeName,
  profileMenu,
  navMenu,
  authMenu,
  rewards,
  onSignInOut,
}) => {
  const navigate = useNavigate();
  const [currentMenu, setCurrentMenu] = useState("");

  const selectCurrentMenu = (menu) => {
    setCurrentMenu(menu);
  };

  const handleItemClick = (item, e) => {
    e?.preventDefault();
    onClosed();
    setTimeout(() => navigate(item.link));
  };

  return (
    <div>
      <Slide direction="right" in={true} mountOnEnter unmountOnExit>
        <Box className={css["menu-box"]}>
          <Grid container>
            <Grid item xs={6} sm={6}>
              {userName && (
                <div className={css["menu-username"]}>{userName}</div>
              )}
              {storeName && (
                <>
                  <span className={css["menu-storetitle"]}>
                    {storeName && (
                      <>
                        <span className={css["menu-storeicon"]}>
                          <StorefrontIcon fontSize="small" />
                        </span>
                        <span className={css["menu-storename"]}>
                          {" "}
                          {storeName}
                        </span>
                      </>
                    )}
                  </span>
                </>
              )}
              {profileTitle && (
                <span className={css["menu-profiletitle"]}>{profileTitle}</span>
              )}
            </Grid>
            <Grid item xs={6} sm={6}>
              {storeLogo && (
                <Box className={css["menu-storelogo"]}>
                  <Image src={storeLogo} alt="logo" noCache={true} />
                </Box>
              )}
            </Grid>
          </Grid>

          {rewards && (
            <RewardsBox
              mode="mobile"
              tier={rewards.tier}
              tierLabel={rewards.tierLabel}
              points={rewards.points}
              isFirstTier={rewards.isFirstTier}
              info={rewards.info}
              expiringPoints={rewards.expiringPoints}
              expiring={rewards.expiring}
              onClosed={onClosed}
            ></RewardsBox>
          )}

          {userName && <div className={css["menu-divider"]}></div>}
          {profileMenu &&
            profileMenu.length > 1 &&
            profileMenu.map((item, i) => (
              <div key={i}>
                <HeaderProfileMobileMenuSection
                  title={item.title}
                  link={item.link}
                  menuItems={item.menuItems}
                  onClick={handleItemClick}
                  currentMenu={currentMenu}
                  selectCurrentMenu={selectCurrentMenu}
                />
              </div>
            ))}
          {profileMenu &&
            profileMenu.length <= 1 &&
            profileMenu[0].menuItems.map((item, i) => (
              <div key={i}>
                <HeaderProfileMobileMenuSection
                  title={item.title}
                  link={item.link}
                  onClick={handleItemClick}
                  currentMenu={currentMenu}
                  selectCurrentMenu={selectCurrentMenu}
                />
              </div>
            ))}
          <div className={css["menu-nav-divider"]}></div>
          {navMenu &&
            navMenu.length &&
            navMenu.map((item, i) => (
              <div key={i}>
                <HeaderProfileMobileMenuSection
                  title={item.title}
                  menuItems={item.menuItems}
                  link={item.link}
                  onClick={handleItemClick}
                  currentMenu={currentMenu}
                  selectCurrentMenu={selectCurrentMenu}
                />
                {i !== navMenu.length - 1 && (
                  <div className={css["menu-divider"]}></div>
                )}
              </div>
            ))}
          <div className={css["menu-divider"]}></div>
          {authMenu &&
            authMenu.length > 0 &&
            authMenu[0].menuItems &&
            authMenu[0].menuItems.map((item, i) => (
              <div key={i}>
                <HeaderProfileMobileMenuSection
                  title={item.title}
                  onClick={() => onSignInOut(item)}
                  currentMenu={currentMenu}
                  selectCurrentMenu={selectCurrentMenu}
                />
                <div className={css["menu-divider"]}></div>
              </div>
            ))}
          {authMenu &&
            authMenu.length > 0 &&
            authMenu[1]?.menuItems &&
            authMenu[1]?.menuItems?.map((item, i) => (
              <div key={i}>
                <HeaderProfileMobileMenuSection
                  title={item.title}
                  link={item.link}
                  onClick={handleItemClick}
                  currentMenu={currentMenu}
                  selectCurrentMenu={selectCurrentMenu}
                />
                <div className={css["menu-divider"]}></div>
              </div>
            ))}
        </Box>
      </Slide>
    </div>
  );
};

HeaderProfileMobileMenu.propTypes = {
  onClosed: PropTypes.func,
  userName: PropTypes.string,
  profileTitle: PropTypes.string,
  storeName: PropTypes.string,
  storeLogo: PropTypes.string,
  profileMenu: PropTypes.array,
  navMenu: PropTypes.array,
  authMenu: PropTypes.array,
  rewards: PropTypes.object,
  onSignInOut: PropTypes.func,
};

/************************************/
/* Header Profile Mobile Menu Section */
/************************************/
const HeaderProfileMobileMenuSection = ({
  title,
  link,
  menuItems,
  onClick,
  currentMenu,
  selectCurrentMenu,
}) => {
  const toggleMenu = () => {
    if (currentMenu === title) {
      selectCurrentMenu("");
      return;
    }
    selectCurrentMenu(title);
  };

  return (
    <>
      {/* no menu items but has a link. Render section as a link */}
      {!menuItems && link && title && (
        <Link
          to={link}
          onClick={(e) => onClick({ link }, e)}
          className={css["menu-section"]}
        >
          <span className={css["menu-section-title"]}>{title}</span>
        </Link>
      )}

      {!menuItems && !link && title && (
        <div
          className={css["menu-section"]}
          onClick={(e) => onClick({ link }, e)}
        >
          <span className={css["menu-section-title"]}>{title}</span>
        </div>
      )}

      {/* no link *or* has menu items. Render section as text and show arrow right if has sub menuItems */}
      {menuItems && (
        <div>
          <div className={css["menu-section"]} onClick={toggleMenu}>
            <span className={css["menu-section-title"]}>{title}</span>
            <span className={css["menu-section-icon"]}>
              {currentMenu === title ? (
                <KeyboardArrowUpRoundedIcon />
              ) : (
                <KeyboardArrowDownRoundedIcon />
              )}
            </span>
          </div>
          {currentMenu === title && (
            <HeaderProfileMobileSubMenuSection
              menuItems={menuItems}
              onItemClick={onClick}
            />
          )}
        </div>
      )}
    </>
  );
};

HeaderProfileMobileMenuSection.propTypes = {
  menuItems: PropTypes.array,
  link: PropTypes.string,
  title: PropTypes.string,
  onClick: PropTypes.func,
  currentMenu: PropTypes.string,
  selectCurrentMenu: PropTypes.func,
};

/************************************/
/* Header Profile Mobile Sub Menu Section*/
/************************************/
const HeaderProfileMobileSubMenuSection = ({ menuItems, onItemClick }) => {
  return (
    <>
      <div className={css["menu-section"]}>
        {menuItems && (
          <List disablePadding sx={{ mt: 0 }}>
            {menuItems.map((item, i) => (
              <ListItem disablePadding key={item.title}>
                <ListItemButton
                  sx={{ pt: 0, pl: 1.25 }}
                  autoFocus={false}
                  onClick={(e) => onItemClick(item, e)}
                >
                  {item.title}
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </div>
    </>
  );
};

HeaderProfileMobileSubMenuSection.propTypes = {
  menuItems: PropTypes.array,
  onItemClick: PropTypes.func,
};
